import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { callApi } from '../api';
import { Section, Stat, ErrorBox, LoadingSpinner } from '../components/Section';
import { Sparkline } from '../components/Sparkline';
import { UserChip } from '../components/UserChip';
import { useSession } from '../hooks/useSession';
import { timeAgo } from '../utils';

const SORTS = [
  { value: 'most_active', label: 'Most active' },
  { value: 'recent_signup', label: 'Recent signups' },
  { value: 'most_reviews', label: 'Most reviews' },
];

export function Users() {
  const { session } = useSession();
  const [series, setSeries] = useState(null);
  const [users, setUsers] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [sort, setSort] = useState('most_active');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  // Active counts: load once on mount.
  useEffect(() => {
    callApi('/api/v1/admin/users/active-counts?days=90', {
      accessToken: session.access_token,
    })
      .then(setSeries)
      .catch((e) => setError(e.message));
  }, [session.access_token]);

  // User list: reload when sort changes; "load more" appends.
  useEffect(() => {
    setLoading(true);
    setUsers([]);
    setCursor(null);
    callApi(`/api/v1/admin/users?sort=${sort}&window=7d&limit=50`, {
      accessToken: session.access_token,
    })
      .then((d) => {
        setUsers(d.users);
        setHasMore(d.has_more);
        setCursor(d.next_cursor);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [session.access_token, sort]);

  const loadMore = async () => {
    try {
      const d = await callApi(
        `/api/v1/admin/users?sort=${sort}&window=7d&limit=50&cursor=${encodeURIComponent(cursor || '')}`,
        { accessToken: session.access_token }
      );
      setUsers((prev) => [...prev, ...d.users]);
      setHasMore(d.has_more);
      setCursor(d.next_cursor);
    } catch (e) {
      setError(e.message);
    }
  };

  const today = series?.rows?.[series.rows.length - 1] || {};

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold text-stone-800">Users</h1>

      <Section title="Active users (last 90 days)">
        {series ? <Sparkline rows={series.rows} height={100} showLegend /> : <LoadingSpinner />}
        <div className="grid grid-cols-3 gap-4 mt-4">
          <Stat value={today.dau ?? 0} label="DAU today" />
          <Stat value={today.wau ?? 0} label="WAU" />
          <Stat value={today.mau ?? 0} label="MAU" />
        </div>
      </Section>

      <Section
        title="Users"
        action={
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="text-sm border rounded px-2 py-1"
          >
            {SORTS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        }
      >
        <ErrorBox message={error} />
        {loading ? <LoadingSpinner /> : (
          <div className="divide-y">
            {users.map((u) => (
              <Link
                key={u.user_id}
                to={`/admin/users/${u.user_id}`}
                className="flex items-center justify-between py-2 hover:bg-stone-50"
              >
                <UserChip
                  userId={u.user_id}
                  displayName={u.display_name}
                  avatarUrl={u.avatar_url}
                />
                <div className="text-right text-xs text-stone-500">
                  <div>{u.activity_count} opens / {u.review_count} reviews</div>
                  <div>{u.phone || '—'} • joined {timeAgo(u.created_at)}</div>
                </div>
              </Link>
            ))}
          </div>
        )}
        {hasMore && (
          <button
            onClick={loadMore}
            className="text-sm text-stone-700 underline mt-2"
          >
            Load more
          </button>
        )}
      </Section>
    </div>
  );
}

